import moment from "moment";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Prompt, useHistory } from "react-router-dom";

import Alert from "veneer/Alert";
import BigContent from "veneer/BigContent";
import Button from "veneer/Button";
import Dialog from "veneer/Dialog";
import Stack from "veneer/Stack";
import styled from "veneer/styled";
import Typography from "veneer/Typography";

import type {
  DeviceMetricFilter,
  DeviceNetworkTypeFilter,
  LastConnectedTier,
} from "cortex/ui/src/fleet/view/device/model";
import { useAppDispatch, useAppSelector } from "cortex/ui/src/hooks";
import { createPromiseApiAction } from "cortex/ui/src/redux-store/api-sagas";
import { getAuthUser } from "cortex/ui/src/redux-store/auth/selectors";
import { lastSeenDeviceCountsSelector } from "cortex/ui/src/redux-store/last-seen-devices/selectors";
import {
  ACCOUNT_STATS_LOAD_BY_DATE,
  deviceAverageUptimeSelector,
} from "cortex/ui/src/redux-store/ui-state/account-stats";
import {
  DASHBOARD_COLUMN_COUNT,
  DashboardGrid,
  GridLayoutItem,
  GridLayouts,
  WidgetCatalogDrawer,
  WidgetCatalogItem,
  WidgetConfigurationDialog,
  WidgetConfigurationValues,
  WidgetGridPosition,
  applyDashboardPositionsToGrid,
  copyDashboardLayout,
  createDashboardWidgetInstanceId,
  getDashboardLayoutFromGrid,
  getFirstAvailableWidgetPosition,
  getHorizontalWidgetSwap,
  getNearestAvailableWidgetPosition,
  useDashboardEditor,
} from "cortex/ui/src/ui/dashboard";
import DeviceMetricsPanel from "cortex/ui/src/ui/device-stats/DeviceMetricsPanel";
import LastSeenStatusPanel from "cortex/ui/src/ui/device-stats/LastSeenStatusPanel";
import { formatPercentage } from "cortex/ui/src/ui/table/formats";
import WsDsConnStatus from "cortex/ui/src/ui/ws-ds-conn-status/WsDsConnStatus";
import { Container, Panel } from "cortex/ui/src/view/layout";

import {
  CONFIGURABLE_DASHBOARD_WIDGET_TYPES,
  DASHBOARD_WIDGET_CATEGORIES,
  DASHBOARD_WIDGET_DEFAULT_HEIGHTS,
  DASHBOARD_WIDGET_DEFAULT_WIDTHS,
  DASHBOARD_WIDGET_DESCRIPTIONS,
  DASHBOARD_WIDGET_TITLES,
  DEFAULT_DASHBOARD_LAYOUT,
  DashboardLayout,
  DashboardWidgetType,
  getDashboardLayoutStorageKey,
  isDashboardLayout,
} from "./dashboardLayout";
import {
  OverviewSummaryWidgets,
  renderOverviewDashboardWidget,
} from "./OverviewDashboardWidgets";
import {
  WidgetConfigurationTarget,
  getConfigurationString,
  getWidgetConfiguration,
  getWidgetConfigurationDetail,
  getWidgetConfigurationDetailLabel,
} from "./widgetConfiguration";

const BigContentCenter = styled(BigContent)({
  textAlign: "center",
});

export default function OverviewPage() {
  const dispatch = useAppDispatch();
  const history = useHistory();
  const averageUptime = useAppSelector(deviceAverageUptimeSelector);
  const lastSeenDeviceCounts = useAppSelector(lastSeenDeviceCountsSelector);
  const user = useAppSelector(getAuthUser);
  const percent = formatPercentage(averageUptime ?? 0, 1);
  const activeAccount = (user as unknown as { account?: { id?: number } })
    ?.account;
  const accountId = user?.account_id ?? activeAccount?.id;
  const storageKey = useMemo(() => {
    if (!user?.id || !accountId) {
      return null;
    }

    return getDashboardLayoutStorageKey(user.id, accountId);
  }, [accountId, user?.id]);
  const {
    clearHistory,
    commitDraftLayout,
    discard: discardDashboardLayout,
    draftLayout,
    draftLayoutRef,
    hasUnsavedChanges,
    layoutHistory,
    redo: redoDashboardLayout,
    replaceDraftLayout,
    reset: resetDashboardLayout,
    save: saveDashboardLayout,
    savedLayout,
    undo: undoDashboardLayout,
  } = useDashboardEditor<DashboardWidgetType>({
    defaultLayout: DEFAULT_DASHBOARD_LAYOUT,
    isValidLayout: isDashboardLayout,
    storageKey,
  });
  const [isCustomizing, setIsCustomizing] = useState(false);
  const [isDiscardDialogOpen, setIsDiscardDialogOpen] = useState(false);
  const [isWidgetCatalogOpen, setIsWidgetCatalogOpen] = useState(false);
  const [configurationTarget, setConfigurationTarget] =
    useState<WidgetConfigurationTarget | null>(null);
  const [draggedCatalogWidget, setDraggedCatalogWidget] =
    useState<WidgetCatalogItem<DashboardWidgetType> | null>(null);
  const dragStartLayoutRef = useRef<DashboardLayout | null>(null);
  const resizeStartLayoutRef = useRef<DashboardLayout | null>(null);
  const activeDragSwapRef = useRef<DashboardLayout | null>(null);
  const pendingSwapLayoutRef = useRef<DashboardLayout | null>(null);
  const pendingExternalDropLayoutRef = useRef<DashboardLayout | null>(null);
  const externalDragActiveRef = useRef(false);
  const ignoreExternalLayoutChangeRef = useRef(false);

  useEffect(() => {
    if (!isCustomizing || !hasUnsavedChanges) {
      return undefined;
    }

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [hasUnsavedChanges, isCustomizing]);

  const handleHideWidget = useCallback(
    (id: string) => {
      const currentLayout = draftLayoutRef.current;
      commitDraftLayout({
        ...currentLayout,
        widgets: currentLayout.widgets.filter((widget) => widget.id !== id),
      });
    },
    [commitDraftLayout],
  );

  const addWidget = useCallback(
    (
      type: DashboardWidgetType,
      config?: Readonly<Record<string, unknown>>,
      position?: WidgetGridPosition,
      baseLayout = draftLayoutRef.current,
    ) => {
      const height = DASHBOARD_WIDGET_DEFAULT_HEIGHTS[type];
      const width = DASHBOARD_WIDGET_DEFAULT_WIDTHS[type];
      const widgetPosition =
        position ??
        getFirstAvailableWidgetPosition(
          baseLayout.widgets,
          width,
          height,
          DASHBOARD_COLUMN_COUNT,
        );
      commitDraftLayout({
        ...baseLayout,
        widgets: [
          ...baseLayout.widgets,
          {
            config,
            height,
            id: CONFIGURABLE_DASHBOARD_WIDGET_TYPES.has(type)
              ? createDashboardWidgetInstanceId(type)
              : type,
            type,
            width,
            ...widgetPosition,
          },
        ],
      });
    },
    [commitDraftLayout],
  );

  const handleAddWidget = useCallback(
    (type: DashboardWidgetType) => {
      if (CONFIGURABLE_DASHBOARD_WIDGET_TYPES.has(type)) {
        setConfigurationTarget({ type });
        return;
      }
      addWidget(type);
    },
    [addWidget],
  );

  const handleDuplicateWidget = useCallback(
    (id: string) => {
      const currentLayout = draftLayoutRef.current;
      const sourceWidget = currentLayout.widgets.find(
        (widget) => widget.id === id,
      );
      if (
        !sourceWidget ||
        !CONFIGURABLE_DASHBOARD_WIDGET_TYPES.has(sourceWidget.type)
      ) {
        return;
      }

      const position = getNearestAvailableWidgetPosition(
        currentLayout.widgets,
        sourceWidget.width,
        sourceWidget.height,
        sourceWidget,
        DASHBOARD_COLUMN_COUNT,
      );
      commitDraftLayout({
        ...currentLayout,
        widgets: [
          ...currentLayout.widgets,
          {
            ...sourceWidget,
            config: sourceWidget.config
              ? { ...sourceWidget.config }
              : undefined,
            id: createDashboardWidgetInstanceId(sourceWidget.type),
            ...position,
          },
        ],
      });
    },
    [commitDraftLayout],
  );

  const handleSaveWidgetConfiguration = useCallback(
    (values: WidgetConfigurationValues) => {
      if (!configurationTarget) {
        return;
      }
      ignoreExternalLayoutChangeRef.current = false;
      const config = getWidgetConfiguration(configurationTarget.type, values);
      if (configurationTarget.id) {
        const currentLayout = draftLayoutRef.current;
        commitDraftLayout({
          ...currentLayout,
          widgets: currentLayout.widgets.map((widget) =>
            widget.id === configurationTarget.id
              ? { ...widget, config }
              : widget,
          ),
        });
      } else {
        addWidget(
          configurationTarget.type,
          config,
          configurationTarget.position,
          configurationTarget.layout,
        );
      }
      setConfigurationTarget(null);
    },
    [addWidget, commitDraftLayout, configurationTarget],
  );

  const handleCancelWidgetConfiguration = useCallback(() => {
    ignoreExternalLayoutChangeRef.current = false;
    setConfigurationTarget(null);
  }, []);

  const handleGridLayoutChange = useCallback(
    (_layout: ReadonlyArray<GridLayoutItem>, layouts: GridLayouts) => {
      if (!isCustomizing || !layouts.lg) {
        return;
      }

      if (externalDragActiveRef.current) {
        return;
      }

      if (ignoreExternalLayoutChangeRef.current) {
        ignoreExternalLayoutChangeRef.current = false;
        return;
      }

      if (pendingExternalDropLayoutRef.current) {
        const droppedLayout = pendingExternalDropLayoutRef.current;
        pendingExternalDropLayoutRef.current = null;
        replaceDraftLayout(droppedLayout);
        return;
      }

      if (pendingSwapLayoutRef.current) {
        const swappedLayout = pendingSwapLayoutRef.current;
        pendingSwapLayoutRef.current = null;
        replaceDraftLayout(swappedLayout);
        return;
      }

      replaceDraftLayout(
        getDashboardLayoutFromGrid(layouts.lg, draftLayoutRef.current),
      );
    },
    [isCustomizing, replaceDraftLayout],
  );

  const handleCatalogWidgetDragStart = useCallback(
    (widget: WidgetCatalogItem<DashboardWidgetType>) => {
      externalDragActiveRef.current = true;
      setDraggedCatalogWidget(widget);
    },
    [],
  );

  const handleCatalogWidgetDragEnd = useCallback(() => {
    externalDragActiveRef.current = false;
    setDraggedCatalogWidget(null);
  }, []);

  const handleCatalogWidgetDrop = useCallback(
    (layout: ReadonlyArray<GridLayoutItem>, item: GridLayoutItem) => {
      const widget = draggedCatalogWidget;
      if (!widget) {
        return;
      }

      const baseLayout = getDashboardLayoutFromGrid(
        layout,
        draftLayoutRef.current,
      );
      const position = { x: item.x, y: item.y };
      externalDragActiveRef.current = false;
      setDraggedCatalogWidget(null);
      setIsWidgetCatalogOpen(false);

      if (CONFIGURABLE_DASHBOARD_WIDGET_TYPES.has(widget.type)) {
        ignoreExternalLayoutChangeRef.current = true;
        setConfigurationTarget({
          layout: baseLayout,
          position,
          type: widget.type,
        });
        return;
      }

      addWidget(widget.type, undefined, position, baseLayout);
      pendingExternalDropLayoutRef.current = copyDashboardLayout(
        draftLayoutRef.current,
      );
    },
    [addWidget, draggedCatalogWidget],
  );

  const handleGridDragStart = useCallback(() => {
    pendingExternalDropLayoutRef.current = null;
    activeDragSwapRef.current = null;
    pendingSwapLayoutRef.current = null;
    dragStartLayoutRef.current = copyDashboardLayout(draftLayout);
  }, [draftLayout]);

  const handleGridDrag = useCallback(
    (
      layout: ReadonlyArray<GridLayoutItem>,
      _oldItem: GridLayoutItem,
      newItem: GridLayoutItem,
      placeholder?: GridLayoutItem,
    ) => {
      const dragStartLayout = dragStartLayoutRef.current;
      if (!dragStartLayout) {
        return;
      }

      const previousSwap = activeDragSwapRef.current;
      const currentPosition = { x: newItem.x, y: newItem.y };
      const swappedLayout = getHorizontalWidgetSwap(dragStartLayout, newItem);
      activeDragSwapRef.current = swappedLayout;
      if (!swappedLayout) {
        if (previousSwap) {
          applyDashboardPositionsToGrid(layout, dragStartLayout);
          const draggedGridItem = layout.find((item) => item.i === newItem.i);
          if (draggedGridItem) {
            draggedGridItem.x = currentPosition.x;
            draggedGridItem.y = currentPosition.y;
          }
          if (placeholder) {
            placeholder.x = currentPosition.x;
            placeholder.y = currentPosition.y;
          }
        }
        return;
      }

      applyDashboardPositionsToGrid(layout, swappedLayout);
      const draggedWidget = swappedLayout.widgets.find(
        (widget) => widget.id === newItem.i,
      );
      if (placeholder && draggedWidget) {
        placeholder.x = draggedWidget.x;
        placeholder.y = draggedWidget.y;
      }
    },
    [],
  );

  const handleGridDragStop = useCallback(
    (
      layout: ReadonlyArray<GridLayoutItem>,
      _oldItem: GridLayoutItem,
      newItem: GridLayoutItem,
    ) => {
      const dragStartLayout = dragStartLayoutRef.current;
      dragStartLayoutRef.current = null;
      if (!dragStartLayout) {
        return;
      }

      const swappedLayout =
        activeDragSwapRef.current ??
        getHorizontalWidgetSwap(dragStartLayout, newItem);
      activeDragSwapRef.current = null;
      if (!swappedLayout) {
        commitDraftLayout(
          getDashboardLayoutFromGrid(layout, draftLayoutRef.current),
          dragStartLayout,
        );
        return;
      }

      applyDashboardPositionsToGrid(layout, swappedLayout);
      pendingSwapLayoutRef.current = swappedLayout;
      commitDraftLayout(swappedLayout, dragStartLayout);
    },
    [commitDraftLayout],
  );

  const handleGridResizeStart = useCallback(() => {
    pendingExternalDropLayoutRef.current = null;
    resizeStartLayoutRef.current = copyDashboardLayout(draftLayoutRef.current);
  }, []);

  const handleGridResizeStop = useCallback(
    (layout: ReadonlyArray<GridLayoutItem>) => {
      const resizeStartLayout = resizeStartLayoutRef.current;
      resizeStartLayoutRef.current = null;
      if (!resizeStartLayout) {
        return;
      }

      commitDraftLayout(
        getDashboardLayoutFromGrid(layout, draftLayoutRef.current),
        resizeStartLayout,
      );
    },
    [commitDraftLayout],
  );

  const handleSaveLayout = useCallback(() => {
    saveDashboardLayout();
    setConfigurationTarget(null);
    setIsWidgetCatalogOpen(false);
    setIsCustomizing(false);
  }, [saveDashboardLayout]);

  const discardLayoutChanges = useCallback(() => {
    discardDashboardLayout();
    setConfigurationTarget(null);
    setIsDiscardDialogOpen(false);
    setIsWidgetCatalogOpen(false);
    setIsCustomizing(false);
  }, [discardDashboardLayout]);

  const handleCancelLayout = useCallback(() => {
    if (hasUnsavedChanges) {
      setIsDiscardDialogOpen(true);
      return;
    }
    discardLayoutChanges();
  }, [discardLayoutChanges, hasUnsavedChanges]);

  const handleStartCustomizing = useCallback(() => {
    clearHistory();
    setIsCustomizing(true);
  }, [clearHistory]);

  const handleUndo = useCallback(() => {
    undoDashboardLayout();
  }, [undoDashboardLayout]);

  const handleRedo = useCallback(() => {
    redoDashboardLayout();
  }, [redoDashboardLayout]);

  const handleResetLayout = useCallback(() => {
    resetDashboardLayout();
  }, [resetDashboardLayout]);

  const handleCloseWidgetCatalog = useCallback(() => {
    setIsWidgetCatalogOpen(false);
  }, []);

  const handleTierClick = useCallback(
    (tier: LastConnectedTier, networkType?: DeviceNetworkTypeFilter) => {
      history.push("/devices", {
        lastConnected: [tier],
        networkType: networkType ?? "Production",
      });
    },
    [history],
  );

  const handleMetricClick = useCallback(
    (metric: DeviceMetricFilter) => {
      history.push("/devices", {
        metric,
      });
    },
    [history],
  );

  useEffect(() => {
    const yesterday = moment().subtract(1, "days").format("YYYY-MM-DD");
    void createPromiseApiAction(dispatch, ACCOUNT_STATS_LOAD_BY_DATE, [
      yesterday,
    ]);
  }, [dispatch]);

  const widgetCatalogCategories = DASHBOARD_WIDGET_CATEGORIES.map(
    (category) => ({
      title: category.title,
      widgets: category.widgetTypes.map((type) => ({
        added: draftLayout.widgets.some((widget) => widget.type === type),
        description: DASHBOARD_WIDGET_DESCRIPTIONS[type],
        height: DASHBOARD_WIDGET_DEFAULT_HEIGHTS[type],
        repeatable: CONFIGURABLE_DASHBOARD_WIDGET_TYPES.has(type),
        title: DASHBOARD_WIDGET_TITLES[type],
        type,
        width: DASHBOARD_WIDGET_DEFAULT_WIDTHS[type],
      })),
    }),
  );
  const configuredWidget = configurationTarget?.id
    ? draftLayout.widgets.find((widget) => widget.id === configurationTarget.id)
    : undefined;
  const summaryWidgets: OverviewSummaryWidgets = {
    "device-metrics": (
      <DeviceMetricsPanel
        onMetricClick={handleMetricClick}
        onNeverConnectedClick={() => handleTierClick("Never Connected")}
      />
    ),
    "last-connected": (
      <LastSeenStatusPanel
        counts={lastSeenDeviceCounts}
        onTierClick={handleTierClick}
      />
    ),
    "total-devices": (
      <Panel mb={0}>
        <Stack
          alignItems="center"
          flex={1}
          height="100%"
          justifyContent="center"
        >
          <BigContent label="Total Devices">
            {lastSeenDeviceCounts.totalDevices.toLocaleString()}
          </BigContent>
        </Stack>
      </Panel>
    ),
    "uptime-average": (
      <Panel mb={0}>
        <Stack
          alignItems="center"
          direction="row"
          flex={1}
          height="100%"
          justifyContent="center"
        >
          <BigContentCenter
            description="Yesterday in production"
            label="Uptime Average"
          >
            {percent}
          </BigContentCenter>
        </Stack>
      </Panel>
    ),
  };

  return (
    <Container
      hideBox
      title="Overview"
      toolbox={
        isCustomizing ? (
          <Stack direction="row" spacing={1}>
            {hasUnsavedChanges && (
              <Alert dense severity="warning">
                Unsaved changes
              </Alert>
            )}
            <Button
              icon="add"
              onClick={() => setIsWidgetCatalogOpen(true)}
              variant="outlined"
            >
              Add widget
            </Button>
            <Button
              disabled={layoutHistory.past.length === 0}
              onClick={handleUndo}
              variant="outlined"
            >
              Undo
            </Button>
            <Button
              disabled={layoutHistory.future.length === 0}
              onClick={handleRedo}
              variant="outlined"
            >
              Redo
            </Button>
            <Button
              icon="revert"
              onClick={handleResetLayout}
              variant="outlined"
            >
              Reset
            </Button>
            <Button onClick={handleCancelLayout} variant="outlined">
              Cancel
            </Button>
            <Button icon="save" onClick={handleSaveLayout}>
              Save
            </Button>
          </Stack>
        ) : (
          <Button
            dataTestId="edit-dashboard"
            icon="edit"
            onClick={handleStartCustomizing}
            variant="outlined"
          >
            Edit
          </Button>
        )
      }
    >
      <Prompt
        message="You have unsaved dashboard changes. Leave without saving?"
        when={isCustomizing && hasUnsavedChanges}
      />
      <WsDsConnStatus />
      <Stack gap={2}>
        <DashboardGrid
          dataTestId="overview-dashboard-grid"
          droppingItem={
            draggedCatalogWidget
              ? {
                  h: draggedCatalogWidget.height,
                  i: `catalog-${draggedCatalogWidget.type}`,
                  w: draggedCatalogWidget.width,
                }
              : undefined
          }
          getWidgetTitle={(type) => DASHBOARD_WIDGET_TITLES[type]}
          isEditing={isCustomizing}
          isWidgetConfigurable={(widget) =>
            CONFIGURABLE_DASHBOARD_WIDGET_TYPES.has(widget.type)
          }
          layout={isCustomizing ? draftLayout : savedLayout}
          onConfigureWidget={(widget) =>
            setConfigurationTarget({ id: widget.id, type: widget.type })
          }
          onDrag={handleGridDrag}
          onDragStart={handleGridDragStart}
          onDragStop={handleGridDragStop}
          onDrop={handleCatalogWidgetDrop}
          onDropDragOver={() =>
            draggedCatalogWidget
              ? {
                  h: draggedCatalogWidget.height,
                  w: draggedCatalogWidget.width,
                }
              : false
          }
          onDuplicateWidget={(widget) => handleDuplicateWidget(widget.id)}
          onHideWidget={(widget) => handleHideWidget(widget.id)}
          onLayoutChange={handleGridLayoutChange}
          onResizeStart={handleGridResizeStart}
          onResizeStop={handleGridResizeStop}
          renderWidget={(widget) =>
            renderOverviewDashboardWidget(widget, summaryWidgets)
          }
        />
      </Stack>
      {configurationTarget && (
        <WidgetConfigurationDialog
          detailLabel={getWidgetConfigurationDetailLabel(
            configurationTarget.type,
          )}
          initialDetail={getWidgetConfigurationDetail(
            configurationTarget.type,
            configuredWidget?.config,
          )}
          initialTitle={getConfigurationString(
            configuredWidget?.config,
            "title",
          )}
          onCancel={handleCancelWidgetConfiguration}
          onSave={handleSaveWidgetConfiguration}
          open
        />
      )}
      <WidgetCatalogDrawer
        categories={widgetCatalogCategories}
        onAddWidget={handleAddWidget}
        onClose={handleCloseWidgetCatalog}
        onWidgetDragEnd={handleCatalogWidgetDragEnd}
        onWidgetDragStart={handleCatalogWidgetDragStart}
        open={isWidgetCatalogOpen}
      />
      <Dialog
        commitLabel="Discard"
        dataTestId={{ component: "discard-dashboard-changes-dialog" }}
        onCancel={() => setIsDiscardDialogOpen(false)}
        onCommit={discardLayoutChanges}
        onDismiss={() => setIsDiscardDialogOpen(false)}
        open={isDiscardDialogOpen}
        title="Discard dashboard changes?"
      >
        <Typography>Your unsaved dashboard changes will be lost.</Typography>
      </Dialog>
    </Container>
  );
}
